<template>
    <div id="date-range-attendance-container">
        <b-row>
            <b-col sm="12">
                <MdTitle v-bind:Text="title" />
            </b-col>
        </b-row>

        <b-row class="mt-2">
            <b-col class="col-12">
                <MdEditableGrid
                    :ref="'editableGrid'"
                    :columns="editableGridFields"
                    :rows="flattenedItems"
                    :showMessageIsEmpty="showMessageIsEmpty"
                    :serverSidePagination="false"
                    :showSelectAll="true"
                    :showBulkEdit="true"
                    :showSearch="true"
                    :validateRowCallback="validateRowHandler"
                    :dataKey="'recID'"
                    :footerRows="footerRows"
                    :frozenColumnAction="true"
                    @cell-edit-complete="cellEditComplete"
                    @bulk-edit-complete="bulkEditComplete"
                    @cell-dbl-click="cellDoubleClick"
                    @cell-click="cellClick"
                    @cell-active="cellActivate"
                >
                </MdEditableGrid>
            </b-col>
        </b-row>

        <div class="space-actions"></div>
        <div class="page-actions">
            <MdButton
                v-bind:Label="$t('Shared.Back')"
                v-bind:ClassProp="'primary btn-login mx-2'"
                v-on:click="gotoDateRangeAttendanceList"
                v-bind:Name="'btn_back'"
            />
            <MdButton
                v-if="!readonly"
                v-bind:Label="$t('DateRangeAttendance.DiagUpdate')"
                v-bind:ClassProp="'primary btn-login mx-2'"
                v-on:click="editDiagnosis"
                v-bind:isReadOnly="readonly || saving || editableItem === defaultItemValue"
                v-bind:Name="'btn_diagnosis'"
            />
            <MdButton
                v-bind:Label="$t('Shared.Save')"
                v-bind:ClassProp="readonly ? 'primary mx-2' : 'primary btn-login mx-2'"
                v-on:click="saveOrEdit"
                v-bind:isReadOnly="readonly || !pendingChanges || saving"
                v-bind:Name="'btn_save'"
            />
        </div>

        <date-range-attendance-modal
            :visible.sync="showEditDialog"
            v-model="editableItem"
            :therapistList="therapistList"
            :facilitiesList="facilitiesList"
            :placeOfServicesList="placeOfServicesList"
            :noteOptions="noteOptions"
            @ok="save"
        >
        </date-range-attendance-modal>

        <diagnosis-update-modal
            :visible.sync="showDiagnosisModal"
            @ok="saveDiagnosis"
            v-model="editableItem"
            :diagnosisList="diagnosisList"
        >
        </diagnosis-update-modal>
    </div>
</template>

<script>
import ShowMessage from '@/components/messages/ShowMessage.js';
import DBFunctions from '@/common/DBFunctions.js';
import ClaimBatch from '@/services/claimbatch.service';
import Utils from '@/common/utils';
import DateRangeAttendanceModal from './dateRangeAttendanceModal.vue';
import DiagnosisUpdateModal from './diagnosisUpdateModal.vue';
import EmployeeService from '@/services/employee.service';
import FacilitiesService from '@/services/facilities.service';
import PlaceOfServiceService from '@/services/placeofservice.service';
import ClaimList from '@/services/claimlist.service';

export default {
    name: 'DateRangeAttendance',
    mixins: [ShowMessage, DBFunctions],
    components: {
        DateRangeAttendanceModal,
        DiagnosisUpdateModal
    },

    data() {
        const defaultItemValue = {
            name: '',
            columns: []
        };

        return {
            defaultItemValue: defaultItemValue,
            OptionsRightMenu: {
                visible: false,
                ItemsList: [],
                btnvisible: false,
                itemdisabled: false,
                PatientCode: '',
                Mode: this.edit_mode,
                favButtonAtion: false
            },
            parameters: {},
            editableItem: defaultItemValue,
            editableGridFields: [
                {
                    key: 'name',
                    label: this.$t('Shared.Name'),
                    dataType: 'text',
                    editable: false,
                    focusable: false,
                    props: {
                        frozen: true
                    }
                }
            ],
            items: [],
            showEditDialog: false,
            showDiagnosisModal: false,
            pendingChanges: false,
            readonly: false,
            saving: false,
            therapistList: [],
            facilitiesList: [],
            placeOfServicesList: [],
            diagnosisList: [],
            noteOptions: {},
            editableItemKey: null,
            pageToReturn: null
        };
    },

    computed: {
        title() {
            return `${this.$t('DateRangeAttendance.EditTitle')} - ${
                this.parameters?.patientName
            } - ${this.parameters?.selectProgramName}`;
        },

        flattenedItems() {
            return this.items.map(item => {
                const row = {
                    recID: this.getRowId(item),
                    name: item.name
                };

                if (item.columns && Array.isArray(item.columns)) {
                    item.columns.forEach(col => {
                        const name = `C${Utils.formatterDateToString(col.transDate).replaceAll(
                            '/',
                            ''
                        )}`;

                        row[name] = col.data ? col.data.unit : '';
                    });
                }

                row['total'] = item.columns.reduce(
                    (acc, column) => (acc += column.data?.unit ?? 0),
                    0
                );
                return row;
            });
        },

        showMessageIsEmpty() {
            return this.items.length === 0;
        },

        footerRows() {
            if (this.items.length !== 0) {
                return [this.createRowFooter(this.$t('DateRangeAttendance.Totals'), this.items)];
            }

            return [];
        }
    },

    methods: {
        getRowId(item) {
            if (item.columns && Array.isArray(item.columns) && item.columns.length !== 0) {
                const columnsValid = item.columns.filter(x => x.data);

                if (columnsValid && columnsValid.length !== 0) {
                    const firstColumn = columnsValid[0].data;

                    return JSON.stringify({
                        courseCode: firstColumn.courseCode.trim(),
                        progCode: firstColumn.progCode.trim()
                    });
                }
            }

            return Utils.generateRecID();
        },

        createRowFooter(name, arr) {
            const acc = {};
            let total = 0;
            arr.forEach(item => {
                if (item.columns && Array.isArray(item.columns)) {
                    item.columns.forEach(col => {
                        const name = `C${Utils.formatterDateToString(col.transDate).replaceAll(
                            '/',
                            ''
                        )}`;
                        if (col.data) {
                            total += Number(col.data.unit);

                            if (Object.prototype.hasOwnProperty.call(acc, name)) {
                                acc[name] += Number(col.data.unit);
                            } else {
                                acc[name] = Number(col.data.unit);
                            }
                        }
                    });
                }
            });
            const item = {};
            this.editableGridFields.forEach(field => {
                item[field.key] = acc[field.key];
            });

            item['name'] = name;
            item['total'] = total;
            return item;
        },

        validateRowHandler() {},

        gotoDateRangeAttendanceList() {
            sessionStorage.setItem('DateRangeAttendancePage', this.pageToReturn);
            this.$router.push({ name: 'date-range-attendance' }).catch(() => {});
        },

        async listingAsync() {
            const parameters = {
                ...this.parameters,
                patientCode: this.$route.params.id,
                programList: this.parameters.selectProgram
            };
            await ClaimBatch.getDateRangeAttendance(parameters)
                .then(response => {
                    if (response.status == '200' && response.data) {
                        this.items = response.data;
                        this.items.forEach(item => {
                            item.columns.sort((a, b) => {
                                return Utils.compareDate(a.transDate, b.transDate);
                            });
                            item.columns.forEach(column => {
                                if (column.data) {
                                    const billable = column.data.attendance === 'Y';
                                    const billed = column.data.billed === 'Y';
                                    let color = '#00FFFFFF';

                                    if (billed) {
                                        color = 'lime';
                                    } else if (billable) {
                                        color = 'red';
                                    } else if (column.data.unit === 0) {
                                        color = 'blue';
                                    }

                                    column.data['cellColor'] = color;
                                    column.data['originalValuesBackup'] = [];
                                }
                            });
                        });

                        if (this.items.length > 0) {
                            const columns = this.items[0].columns;
                            columns.forEach(element => {
                                this.editableGridFields.push({
                                    key: `C${Utils.formatterDateToString(
                                        element.transDate
                                    ).replaceAll('/', '')}`,
                                    label: Utils.formatterDateToString(element.transDate),
                                    dataType: 'numeric',
                                    editable: true,
                                    class: 'text-right',
                                    props: {
                                        min: 0,
                                        inputType: 'integer',
                                        isCellEditableCallback: this.isCellEditable,
                                        cellStyleCallback: this.cellStyleCallback
                                    }
                                });
                            });
                            this.editableGridFields.push({
                                key: 'total',
                                label: this.$t('DateRangeAttendance.Totals'),
                                dataType: 'text',
                                editable: false,
                                focusable: false,
                                props: {
                                    frozen: true,
                                    alignFrozen: 'right',
                                    cellClass: 'font-weight-bold'
                                }
                            });
                        }
                    }

                    this.ShowMessageIsEmpty = this.items.length === 0;
                    this.editableItem = this.defaultItemValue;
                })
                .catch(error => {
                    this.editableItem = this.defaultItemValue;
                    this.showResponseErrorMessage(error);
                });
        },

        isCellEditable({ rowKey, column }) {
            const rowIndex = this.indexOfFromItemRowKey(rowKey);
            if (rowIndex !== -1) {
                const data = this.items[rowIndex].columns[column - 1].data;

                if (data) {
                    const unattended =
                        Utils.isNullOrEmptyString(data.attendance) || data.attendance !== 'Y';
                    const billable = data.attendance === 'Y';
                    const billed = data.billed === 'Y';
                    return (billable || unattended) && !billed;
                }
            }

            return false;
        },

        cellStyleCallback({ rowKey, column }) {
            const rowIndex = this.indexOfFromItemRowKey(rowKey);
            if (rowIndex !== -1) {
                const data = this.items[rowIndex].columns[column - 1].data;

                if (data) {
                    return [
                        {
                            borderRadius: '6px',
                            boxShadow: `inset 0 0 0 1px ${data.cellColor}, inset -1px 0 0 0 white`
                        }
                    ];
                }
            }

            return [
                {
                    boxShadow: 'inset 0 0 0 1px gray',
                    background: 'lightgray !important',
                    borderRadius: '6px'
                }
            ];
        },

        async editDiagnosis() {
            await this.getVisitDiagnosisCodes(null);
            this.showDiagnosisModal = true;
        },

        edit(item, editableItemKey) {
            this.editableItem = item;
            this.editableItemKey = editableItemKey;
            this.showEditDialog = true;
        },

        updateItemData(item, props) {
            const { row, column } = this.editableItemKey;
            const data = this.items[row].columns[column].data;

            props.forEach(f => {
                if (data[f] !== item[f]) {
                    this.pendingChanges = true;
                    this.updateOriginalValuesBackup(row, column, f, data[f], item[f]);
                    data[f] = item[f];
                }
            });
        },

        save(item) {
            this.updateItemData(item, [
                'facilityCode',
                'posCode',
                'therapistCode',
                'timeFrom',
                'timeTo'
            ]);
        },

        saveDiagnosis(item) {
            this.updateItemData(item, ['diagnosticCode']);
        },

        async saveOrEdit() {
            this.saving = true;
            const data = this.items.reduce((acc, item) => {
                const columns = item.columns
                    .filter(c => c.data?.originalValuesBackup?.length !== 0)
                    .map(c => {
                        const rest = { ...c.data };
                        delete rest.originalValuesBackup;
                        delete rest.cellColor;
                        return rest;
                    });

                return acc.concat(columns);
            }, []);

            await ClaimBatch.updateDateRangeAttendance({ attendanceDateRanges: data })
                .then(response => {
                    if (response.status == '200') {
                        this.pendingChanges = false;
                        this.gotoDateRangeAttendanceList();

                        setTimeout(
                            () =>
                                this.$bvToast.toast(this.$t('Shared.SavedSuccessfully'), {
                                    title: this.$t('Msg.Success'),
                                    autoHideDelay: 5000,
                                    variant: 'success'
                                }),
                            50
                        );
                    }
                    this.saving = false;
                })
                .catch(error => {
                    this.saving = false;
                    this.showResponseErrorMessage(error);
                });
        },

        async initializeData() {
            try {
                const stored = sessionStorage.getItem('DateRangeAttendanceFilters');

                if (!stored) {
                    this.$router.back();
                    return;
                }

                this.parameters = JSON.parse(stored);
                this.pageToReturn = this.parameters.currentPage;
                delete this.parameters.currentPage;

                await this.listingAsync();

                await Promise.allSettled([
                    this.getAllEmployee(),
                    this.getAllFacilities(),
                    this.getAllPlaceOfServices(),
                    this.getNoteOption()
                ]);
            } catch (error) {
                this.$router.push({ name: 'date-range-attendance' });
            }
        },
        bulkEditComplete({ rows, editedValues }) {
            rows.forEach(row => {
                const rowIndex = this.indexOfFromItemRowKey(row.recID);
                if (rowIndex === -1) return;

                Object.entries(editedValues).forEach(([colKey, val]) => {
                    const colFieldIndex = this.editableGridFields.findIndex(f => f.key === colKey);
                    if (colFieldIndex === -1) return;

                    const columnIndex = colFieldIndex - 1; // offset: 'name' is at field index 0
                    const colData = this.items[rowIndex].columns[columnIndex]?.data;
                    if (!colData) return;

                    const oldValue = colData.unit;
                    if (oldValue !== val) {
                        colData.unit = val;
                        this.pendingChanges = true;
                        this.updateOriginalValuesBackup(
                            rowIndex,
                            columnIndex,
                            'unit',
                            oldValue,
                            val
                        );
                    }
                });
            });
        },

        setupEventListeners() {},

        cleanupEventListeners() {
            sessionStorage.removeItem('DateRangeAttendanceFilters');
        },

        indexOfFromItemRowKey(rowKey) {
            const key = JSON.parse(rowKey);
            return this.items.findIndex(x =>
                x.columns.some(
                    y =>
                        y.data &&
                        y.data.courseCode.trim() === key.courseCode &&
                        y.data.progCode.trim() === key.progCode
                )
            );
        },

        cellEditComplete({ rowKey, column, value }) {
            const rowIndex = this.indexOfFromItemRowKey(rowKey);

            if (rowIndex !== -1) {
                const oldValue = this.items[rowIndex].columns[column - 1].data.unit;

                if (oldValue !== value) {
                    this.items[rowIndex].columns[column - 1].data.unit = value;
                    this.pendingChanges = true;
                    this.updateOriginalValuesBackup(rowIndex, column - 1, 'unit', oldValue, value);
                }
            }
        },

        updateOriginalValuesBackup(rowIndex, columnIndex, propName, propValue, propNewValue) {
            const dataColumn = this.items[rowIndex].columns[columnIndex].data;
            const backup = dataColumn.originalValuesBackup;

            const index = backup.findIndex(x => Object.prototype.hasOwnProperty.call(x, propName));

            if (index === -1) {
                backup.push({ [propName]: propValue });
                return;
            }

            if (backup[index][propName] === propNewValue) {
                backup.splice(index, 1);
            }
        },

        cellDoubleClick(data) {
            if (
                !['name', 'total'].includes(data.columnKey) &&
                this.isCellEditable({ rowKey: data.rowKey, column: data.column })
            ) {
                const rowIndex = this.indexOfFromItemRowKey(data.rowKey);

                if (rowIndex !== -1) {
                    const item = this.items[rowIndex].columns[data.column - 1].data;

                    if (item) {
                        this.edit(item, { row: rowIndex, column: data.column - 1 });
                    }
                }
            }
        },

        cellClick(data) {
            const { columnKey, rowKey, column } = data;
            this.editableItem = this.defaultItemValue;

            if (
                !['name', 'total'].includes(columnKey) &&
                this.isCellEditable({ rowKey: rowKey, column: column })
            ) {
                const rowIndex = this.indexOfFromItemRowKey(rowKey);

                if (rowIndex !== -1) {
                    const item = this.items[rowIndex].columns[column - 1].data;

                    if (item) {
                        this.editableItem = item;
                        this.editableItemKey = { row: rowIndex, column: column - 1 };
                    }
                }
            }
        },

        cellActivate(data) {
            if (data) {
                const { columnKey, rowKey, column } = data;

                if (
                    !['name', 'total'].includes(columnKey) &&
                    this.isCellEditable({ rowKey: rowKey, column: column })
                ) {
                    const rowIndex = this.indexOfFromItemRowKey(rowKey);

                    if (rowIndex !== -1) {
                        const editableItem = this.items[rowIndex].columns[column - 1].data;

                        if (editableItem) {
                            this.editableItem = editableItem;
                            this.editableItemKey = { row: rowIndex, column: column - 1 };
                        } else {
                            this.editableItem = this.defaultItemValue;
                            this.editableItemKey = null;
                        }
                    }
                }
            } else {
                this.editableItem = this.defaultItemValue;
                this.editableItemKey = null;
            }
        },

        async getAllEmployee() {
            await EmployeeService.getEmployeeLookupByType()
                .then(response => {
                    if (response.status == '200' && response.data) {
                        this.therapistList = response.data;
                    }
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        },

        async getAllFacilities() {
            await FacilitiesService.getFacilitiesLookup()
                .then(response => {
                    if (response.status == '200' && response.data) {
                        this.facilitiesList = response.data;
                    }
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        },

        async getAllPlaceOfServices() {
            await PlaceOfServiceService.getPlaceOfServiceLookup()
                .then(response => {
                    if (response.status == '200' && response.data) {
                        this.placeOfServicesList = response.data;
                    }
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        },

        async getNoteOption() {
            this.noteOptions = await this.getNoteOptions();
        },

        async getVisitDiagnosisCodes(serviceDate) {
            await ClaimList.GetVisitDiagnosisCodes(this.$route.params.id, serviceDate)
                .then(response => {
                    if (response.data) {
                        var diag = response.data;
                        this.diagnosisList = diag.map(element => ({
                            code: element.code ? element.code.trim() : '',
                            description: element.description ? element.description.trim() : ''
                        }));
                    }
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        }
    },

    created() {
        this.initializeData();
        this.setupEventListeners();
    },

    mounted() {
        this.OptionsRightMenu.ItemsList = [];
        let layoutStatus = this.$store.state.layoutStatus;

        switch (layoutStatus) {
            case 'L0R1':
                this.OptionsRightMenu.favButtonAtion = false;
                this.$emit('getVisibleMenu', this.OptionsRightMenu);
                break;
            case 'L1R0':
            case 'L1R1':
                this.OptionsRightMenu.btnvisible = false;
                this.$emit('getVisibleMenu', this.OptionsRightMenu);
                break;
            default:
                this.OptionsRightMenu.favButtonAtion = false;
                this.$emit('getVisibleMenu', this.OptionsRightMenu);
        }
    },

    destroyed() {
        this.OptionsRightMenu.ItemsList = [];
        this.OptionsRightMenu.Mode = false;
        this.OptionsRightMenu.visible = false;
        this.$emit('getVisibleMenu', this.OptionsRightMenu);
    },

    beforeDestroy() {
        this.cleanupEventListeners();
    },

    beforeRouteLeave(to, from, next) {
        if (!this.readonly && this.pendingChanges) {
            this.$bvModal
                .msgBoxConfirm(this.$t('Msg.LeavingWithoutSaving'), {
                    title: this.$t('DateRangeAttendance.Title'),
                    okTitle: this.$t('Shared.BtnSave'),
                    cancelTitle: this.$t('Shared.BtnDismiss'),
                    footerClass: 'p-2',
                    titleClass: 'modal-title modal-type-warning'
                })
                .then(async value => {
                    if (!value) {
                        next();
                    } else {
                        await this.saveOrEdit();

                        if (!this.pendingChanges) {
                            next();
                        }
                    }
                });
        } else {
            next();
        }
    }
};
</script>
